// Shared report controls: preset+custom date range, and a multi-select filter.
import { useState } from "react";
import { Filter, X, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

export function isoToday() { return new Date().toISOString().slice(0, 10); }
export function isoDaysAgo(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export const RANGE_PRESETS = [
  { id: "today", label: "Today" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "90d", label: "Last 90 days" },
  { id: "custom", label: "Custom range…" },
];

// Build the request params for a preset/custom range.
export function rangeParams(rng, from, to) {
  if (rng === "custom" && from && to) return { date_from: from, date_to: to };
  return { range_str: rng };
}

// A preset selector + (when "custom") two inline date pickers.
export function RangeControl({ rng, setRng, from, to, setFrom, setTo, testid = "range" }) {
  return (
    <div className="flex items-center gap-2">
      <Select value={rng} onValueChange={setRng}>
        <SelectTrigger className="w-44" data-testid={`${testid}-preset`}>
          <CalendarDays className="w-3.5 h-3.5 mr-1 opacity-70" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {RANGE_PRESETS.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {rng === "custom" && (
        <div className="flex items-center gap-1.5">
          <input type="date" value={from} max={to || isoToday()} onChange={(e) => setFrom(e.target.value)}
            className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid={`${testid}-from`} />
          <span className="text-muted-foreground text-xs">to</span>
          <input type="date" value={to} min={from} max={isoToday()} onChange={(e) => setTo(e.target.value)}
            className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid={`${testid}-to`} />
        </div>
      )}
    </div>
  );
}

// Compact multi-select (Popover + checkboxes).
export function MultiSelect({ label, options, selected, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const toggle = (id) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const count = selected.length;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 h-9" data-testid={testid}>
          <Filter className="w-3.5 h-3.5" />
          {label}{count > 0 && <Badge className="ml-1 h-5 px-1.5 bg-primary text-primary-foreground text-[10px]">{count}</Badge>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 max-h-72 overflow-auto" align="start">
        {count > 0 && (
          <button className="text-xs text-primary hover:underline mb-2 flex items-center gap-1"
            onClick={() => onChange([])} data-testid={`${testid}-clear`}>
            <X className="w-3 h-3" /> Clear ({count})
          </button>
        )}
        {(!options || options.length === 0) && <div className="text-xs text-muted-foreground px-1 py-2">No options</div>}
        <div className="space-y-1">
          {(options || []).map((o) => (
            <label key={o.id} className="flex items-center gap-2 text-sm px-1.5 py-1 rounded hover:bg-muted/50 cursor-pointer">
              <Checkbox checked={selected.includes(o.id)} onCheckedChange={() => toggle(o.id)} data-testid={`${testid}-opt-${o.id}`} />
              <span className="truncate">{o.name}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
